import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { useAppData } from '@/app-data';
import { useEpics, EPIC_DRAG_MIME, UNASSIGNED_EPIC, setActiveDragEpic } from '@/features/backlog/epics';
import { useItemPanel } from '@/components/item-panel';
import * as api from '@/features/backlog/api';
import { Item } from '@/features/backlog/types';
import { cn } from '@/components/ui/cn';
import { Check, Grip, Layers, OpenPanel, Plus, Search, X } from '@/components/ui/icons';
import { TaskTypeGlyph, UnassignedGlyph } from '@/components/ui/icons';
import { Popover } from '@/components/ui/Popover';
import { Spinner } from '@/components/ui/Spinner';
import { EPIC_COLORS, epicColor, taskTypeMeta } from '@/features/backlog/constants';
import { closedLabel } from '@/components/status-dot';

/** Width (px) of the epic sidebar. Mirrored by the AppLayout content inset (paddingLeft) so the
 *  board reflows to sit beside it, like the right details dock. */
export const SIDEBAR_WIDTH = 268;

/**
 * The toggle-able LEFT sidebar listing the current board's epics. Each epic is a draggable chip —
 * drag it onto a table row / kanban card / timeline row to link that item to the epic (the views
 * own the drop). Clicking a chip focuses it (the views dim items not linked to it); the ⤢ button
 * opens the epic's details; "New epic" opens a pre-typed create form. Lives in AppLayout so it is
 * shared across the Table/Kanban/Timeline views and reflows the content beside it.
 */
export default function EpicSidebar() {
  const { epics, linkedCount, epicProgress, unassignedCount, setSidebarOpen, focusedEpicId, setFocusedEpic, setDraggingEpicId } = useEpics();
  const { openItem } = useItemPanel();

  // Optimistic-overlay mirror: a drag-reorder / recolour shows instantly, but we do NOT blindly
  // replace the list on every provider change — an unrelated board edit re-emits `epics` and would
  // otherwise snap a just-dragged epic back to its old slot before its own echo lands. Instead we
  // keep the pending optimistic values in a ref, re-apply them on top of the provider list, and drop
  // each expectation the moment the incoming provider value matches it (the echo arrived).
  const pendingRef = useRef<Map<string, { epic_rank?: number; color?: string | null }>>(new Map());
  const eff = (it: Item) => it.epic_rank ?? it.number;
  const applyPending = useCallback((base: Item[]): Item[] => {
    const pend = pendingRef.current;
    const merged = base.map((e) => {
      const p = pend.get(e.id);
      if (!p) return e;
      const rankLanded = p.epic_rank === undefined || e.epic_rank === p.epic_rank;
      const colorLanded = p.color === undefined || (e.color ?? null) === (p.color ?? null);
      if (rankLanded && colorLanded) {
        pend.delete(e.id);
        return e;
      }
      return {
        ...e,
        ...(p.epic_rank !== undefined && !rankLanded ? { epic_rank: p.epic_rank } : {}),
        ...(p.color !== undefined && !colorLanded ? { color: p.color } : {}),
      };
    });
    // Re-sort by the same key the provider uses so an optimistic rank moves the epic to its slot.
    return merged.sort((a, b) => eff(a) - eff(b) || a.number - b.number);
  }, []);

  const [list, setList] = useState<Item[]>(() => applyPending(epics));
  useEffect(() => setList(applyPending(epics)), [epics, applyPending]);

  // Quick filter: narrow the rail by epic title or key. While filtering, drag-reorder is disabled
  // (reordering a subset would compute ranks against the wrong neighbours).
  const [filter, setFilter] = useState('');
  const fq = filter.trim().toLowerCase();
  const filtered = useMemo(
    () => (fq ? list.filter((e) => (e.title || '').toLowerCase().includes(fq) || (e.key || '').toLowerCase().includes(fq)) : list),
    [list, fq],
  );

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // Persist a rail reorder: give the moved epic a rank between its new neighbours' effective ranks
  // (epic_rank ?? number), the same fractional-midpoint trick used for item positions.
  const onReorder = (e: DragEndEvent) => {
    if (fq) return; // no reordering while a filter is applied
    const from = list.findIndex((x) => x.id === e.active.id);
    const to = e.over ? list.findIndex((x) => x.id === e.over!.id) : -1;
    if (from === -1 || to === -1 || from === to) return;
    const next = arrayMove(list, from, to);
    const prev = next[to - 1] ? eff(next[to - 1]) : undefined;
    const after = next[to + 1] ? eff(next[to + 1]) : undefined;
    let rank: number;
    if (prev == null && after == null) return; // only child — nothing to reorder against
    else if (prev == null) rank = after! - 1;
    else if (after == null) rank = prev + 1;
    else rank = (prev + after) / 2;
    const moved = next[to];
    const cur = pendingRef.current.get(moved.id) ?? {};
    pendingRef.current.set(moved.id, { ...cur, epic_rank: rank });
    setList(applyPending(epics.map((x) => (x.id === moved.id ? { ...x, epic_rank: rank } : x))));
    api.setItemEpicRank(moved.id, rank).catch(() => {
      pendingRef.current.delete(moved.id);
      setList(applyPending(epics));
    });
  };

  const recolor = (epic: Item, color: string | null) => {
    const cur = pendingRef.current.get(epic.id) ?? {};
    pendingRef.current.set(epic.id, { ...cur, color });
    setList(applyPending(epics.map((x) => (x.id === epic.id ? { ...x, color } : x))));
    api.setItemColor(epic.id, color).catch(() => {
      const p = pendingRef.current.get(epic.id);
      if (p) {
        delete p.color;
        if (p.epic_rank === undefined) pendingRef.current.delete(epic.id);
      }
      setList(applyPending(epics));
    });
  };

  return (
    <aside
      className="fixed bottom-0 left-0 top-14 z-20 flex flex-col border-r border-line bg-surface"
      style={{ width: SIDEBAR_WIDTH }}
      aria-label="Epics"
    >
      <div className="flex items-center gap-2 border-b border-line px-3 py-2.5">
        <Layers className="text-fg-faint" />
        <span className="text-[13px] font-semibold text-fg-strong">Epics</span>
        <span className="rounded-full bg-layer px-1.5 py-0.5 text-[11px] font-semibold text-fg-faint">{epics.length}</span>
        <button
          type="button"
          onClick={() => setSidebarOpen(false)}
          aria-label="Hide epics sidebar"
          title="Hide epics"
          className="ml-auto flex h-7 w-7 items-center justify-center rounded-md text-fg-ghost transition-colors hover:bg-layer-strong hover:text-fg"
        >
          <X />
        </button>
      </div>

      <div className="space-y-2 border-b border-line p-2">
        <NewEpicComposer />
        {epics.length > 4 ? (
          <div className="flex h-8 items-center gap-1.5 rounded-lg border border-line bg-surface-inset px-2 focus-within:border-accent/50">
            <Search className="h-3.5 w-3.5 shrink-0 text-fg-faint" />
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter epics…"
              aria-label="Filter epics"
              className="min-w-0 flex-1 bg-transparent text-[12.5px] text-fg placeholder:text-fg-ghost focus:outline-none"
            />
            {filter ? (
              <button type="button" onClick={() => setFilter('')} aria-label="Clear filter" className="shrink-0 text-fg-faint transition-colors hover:text-fg-soft">
                <X className="h-3.5 w-3.5" />
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="thin-scroll min-h-0 flex-1 overflow-y-auto p-2">
        {/* Always-topmost helper: focus the items that belong to no epic (hidden while filtering). */}
        {!fq ? (
          <div className="mb-1">
            <UnassignedRow
              active={focusedEpicId === UNASSIGNED_EPIC}
              count={unassignedCount}
              onFocus={() => setFocusedEpic(focusedEpicId === UNASSIGNED_EPIC ? null : UNASSIGNED_EPIC)}
            />
          </div>
        ) : null}
        {list.length === 0 ? (
          <p className="px-2 py-8 text-center text-[12px] leading-relaxed text-fg-faint">
            No epics yet. Set an item's <span className="text-fg-muted">Type</span> to{' '}
            <b className="text-fg-soft">Epic</b>, or use <b className="text-fg-soft">New epic</b> above — then
            drag epics onto rows and cards to link them.
          </p>
        ) : filtered.length === 0 ? (
          <p className="px-2 py-8 text-center text-[12px] text-fg-faint">No epics match “{filter.trim()}”.</p>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onReorder}>
            <SortableContext items={filtered.map((e) => e.id)} strategy={verticalListSortingStrategy}>
              <ul className="space-y-1">
                {filtered.map((epic) => (
                  <EpicRow
                    key={epic.id}
                    epic={epic}
                    active={focusedEpicId === epic.id}
                    count={linkedCount(epic.id)}
                    closed={epicProgress(epic.id).closed}
                    dropped={epicProgress(epic.id).dropped}
                    onFocus={() => setFocusedEpic(focusedEpicId === epic.id ? null : epic.id)}
                    onOpen={() => openItem(epic.id)}
                    onRecolor={recolor}
                    setDraggingEpicId={setDraggingEpicId}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {focusedEpicId ? (
        <button
          type="button"
          onClick={() => setFocusedEpic(null)}
          className="border-t border-line px-3 py-2 text-left text-[12px] text-fg-muted transition-colors hover:bg-layer hover:text-fg"
        >
          Clear highlight
        </button>
      ) : null}
    </aside>
  );
}

/**
 * The always-first rail card that focuses items belonging to NO epic. Unlike an EpicRow it is a pure
 * filter toggle — not draggable, reorderable, recolourable or openable — with a generic gray "?" glyph.
 * The grip-width spacer keeps its glyph aligned with the epic glyphs below it.
 */
function UnassignedRow({ active, count, onFocus }: { active: boolean; count: number; onFocus: () => void }) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onFocus}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onFocus();
        }
      }}
      title="Show items not linked to any epic"
      className={cn(
        'flex cursor-pointer items-center gap-1 rounded-lg border pr-1.5 transition-colors',
        active ? 'border-accent/60 bg-accent/10' : 'border-line bg-surface-inset hover:bg-layer',
      )}
    >
      <span className="w-6 shrink-0" aria-hidden />
      <div className="flex min-w-0 flex-1 items-center gap-2 py-2">
        <UnassignedGlyph className="shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-medium text-fg">Unassigned</div>
          <div className="mt-0.5 text-[11px] text-fg-faint">
            {count} {count === 1 ? 'item' : 'items'}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * One epic in the rail. It carries TWO independent drags: a dnd-kit sortable on the small grip
 * handle REORDERS the rail (writes epic_rank), while the body stays a native HTML5 draggable that
 * LINKS the epic onto a row/card. The two never collide because dnd-kit's listeners live only on the
 * grip (a non-draggable button), and the native draggable is a separate sibling element. Clicking
 * the body focuses the epic; the colour dot and open button are hover affordances.
 */
function EpicRow({
  epic,
  active,
  count,
  closed,
  dropped,
  onFocus,
  onOpen,
  onRecolor,
  setDraggingEpicId,
}: {
  epic: Item;
  active: boolean;
  count: number;
  closed: number;
  dropped: number;
  onFocus: () => void;
  onOpen: () => void;
  onRecolor: (epic: Item, color: string | null) => void;
  setDraggingEpicId: (id: string | null) => void;
}) {
  const pct = count > 0 ? Math.round((closed / count) * 100) : 0;
  const allDone = count > 0 && closed >= count;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: epic.id });
  const style = { transform: CSS.Translate.toString(transform), transition };
  const color = epicColor(epic);
  return (
    <li ref={setNodeRef} style={style} className={cn(isDragging && 'relative z-10 opacity-60')}>
      <div
        className={cn(
          // items-center keeps the colour dot + open icon vertically centred; the grip overrides
          // with self-stretch to stay a full-height drag zone.
          'group flex items-center gap-1 rounded-lg border pr-1.5 transition-colors',
          active ? 'border-accent/60 bg-accent/10' : 'border-line bg-surface-inset hover:bg-layer',
        )}
      >
        {/* Reorder handle (dnd-kit pointer drag) — its listeners never touch the native link-drag. */}
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="flex w-6 shrink-0 cursor-grab touch-none items-center justify-center self-stretch rounded-l-lg text-fg-ghost opacity-0 transition-opacity hover:text-fg-soft focus-visible:opacity-100 active:cursor-grabbing group-hover:opacity-100"
          aria-label={`Reorder ${epic.key}`}
          title="Drag to reorder the rail"
        >
          <Grip />
        </button>

        {/* Link-drag body (native HTML5 DnD) + click-to-focus. */}
        <div
          draggable
          onDragStart={(e) => {
            e.dataTransfer.setData(EPIC_DRAG_MIME, epic.id);
            e.dataTransfer.setData('text/plain', `${epic.key} ${epic.title}`);
            e.dataTransfer.effectAllowed = 'link';
            setDraggingEpicId(epic.id);
            // Synchronous, dataTransfer-independent marker read by the drop targets (WebKit doesn't
            // reliably expose custom MIME types on dragover — see useEpicDropTarget).
            setActiveDragEpic(epic.id);
            // IMPERATIVE, synchronous marker for the drag: a body class (not React state) so the drop
            // targets are made drop-ready in the SAME tick — before the native dragover/drop fire. A
            // React re-render (setDraggingEpicId) can lag behind the native drag under React 18, which is
            // why the kanban cards (whose locked content is pointer-events:none) didn't receive the drop.
            document.body.classList.add('epic-dragging');
          }}
          onDragEnd={() => {
            setDraggingEpicId(null);
            setActiveDragEpic(null);
            document.body.classList.remove('epic-dragging');
          }}
          onClick={onFocus}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onFocus();
            }
          }}
          title="Drag onto rows/cards to link · click to highlight linked items"
          className="flex min-w-0 flex-1 cursor-grab items-center gap-2 py-2 active:cursor-grabbing"
        >
          <TaskTypeGlyph type="epic" color={color} className="shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="truncate text-[13px] font-medium text-fg">{epic.title || 'Untitled epic'}</div>
            <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-fg-faint">
              <span className="font-semibold">{epic.key}</span>
              <span aria-hidden>·</span>
              <span>{count} linked</span>
            </div>
            {/* Subtle completion sense: a thin bar + faint %, only once the epic has linked items. */}
            {count > 0 ? (
              <div className="mt-1.5 flex items-center gap-1.5" title={closedLabel(closed, dropped, count)}>
                <div className="h-[3px] flex-1 overflow-hidden rounded-full bg-layer-strong">
                  <div
                    className={cn('h-full rounded-full transition-[width] duration-500 ease-out', allDone ? 'bg-emerald-500/80' : 'bg-accent/60')}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="tnum text-[10px] font-medium text-fg-ghost">{pct}%</span>
              </div>
            ) : null}
          </div>
        </div>

        <EpicColorButton epic={epic} color={color} onRecolor={onRecolor} />

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
          className="flex shrink-0 items-center self-center rounded p-1 text-fg-ghost opacity-0 transition-opacity hover:text-fg focus-visible:opacity-100 group-hover:opacity-100"
          aria-label={`Open ${epic.key}`}
          title="Open epic details"
        >
          <OpenPanel />
        </button>
      </div>
    </li>
  );
}

/** The per-epic colour swatch + palette popover. Picking a swatch (or "Default colour") writes the
 *  epic's colour via the narrow endpoint so its chip re-tints everywhere. Portaled so the sidebar's
 *  own scroll can't clip it. */
function EpicColorButton({
  epic,
  color,
  onRecolor,
}: {
  epic: Item;
  color: string;
  onRecolor: (epic: Item, color: string | null) => void;
}) {
  return (
    <Popover
      portal
      align="right"
      panelClassName="min-w-0"
      trigger={({ toggle }) => (
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            toggle();
          }}
          className="flex shrink-0 items-center self-center rounded p-1 opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100"
          aria-label={`Colour ${epic.key}`}
          title="Set epic colour"
        >
          <span className="h-3.5 w-3.5 rounded-full ring-1 ring-inset ring-black/25" style={{ background: color }} />
        </button>
      )}
    >
      {(close) => (
        <div className="p-1">
          <div className="grid grid-cols-6 gap-1.5">
            {EPIC_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => {
                  onRecolor(epic, c);
                  close();
                }}
                className="flex h-7 w-7 items-center justify-center rounded-full ring-1 ring-inset ring-black/25 transition-transform hover:scale-110"
                style={{ background: c }}
                aria-label={`Colour ${c}`}
              >
                {(epic.color ?? '').toLowerCase() === c.toLowerCase() ? <Check className="text-black/80" /> : null}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => {
              onRecolor(epic, null);
              close();
            }}
            className="mt-1.5 w-full border-t border-line px-2 pt-2 text-left text-[12px] text-fg-faint transition-colors hover:text-fg-soft"
          >
            Default colour
          </button>
        </div>
      )}
    </Popover>
  );
}

/**
 * Inline "add epic" affordance for the rail: the dashed button expands into a text field so an epic
 * is named and created right here — no drawer round-trip (the old New button opened the details
 * panel in create mode, which was awkward when the epic lives on the left). Enter creates it and
 * stays open for the next; Escape or clicking away empty closes it; a non-empty blur commits. The
 * EpicsProvider's own live item feed (plus this reload) makes the new epic appear in the list at once.
 */
function NewEpicComposer() {
  const { currentBoardId } = useAppData();
  const { reload } = useEpics();
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState('');
  const [pending, setPending] = useState(0); // creates in flight — drives the spinner
  const inputRef = useRef<HTMLInputElement>(null);
  // Escape closes the field; because that unmounts the input it also fires blur — this flag lets the
  // blur handler tell "cancelled" from "clicked away", so Escape never commits the typed title.
  const escaping = useRef(false);

  // Fire a create for a title the caller has ALREADY captured and cleared from the field. Because the
  // field was cleared synchronously (see `submit`), the round-trip never races the next title the user
  // types, and several creates can be in flight at once — each epic is independent, so a rapid second
  // Enter is never dropped. Best-effort, mirroring the drawer's inline epic create; the EpicsProvider's
  // live item feed plus this reload surface the new epic in the list.
  const fireCreate = (t: string) => {
    if (!currentBoardId) return;
    setPending((n) => n + 1);
    api
      .createItem({ board_id: currentBoardId, title: t, type: 'epic' })
      .then(() => reload())
      .catch(() => undefined)
      .finally(() => setPending((n) => n - 1));
  };

  // Commit the current title (Enter, or a non-empty blur): clear the field FIRST so the next epic
  // types into a fresh box with nothing to clobber, then create the captured title. Returns whether
  // there was anything to submit.
  const submit = () => {
    const t = title.trim();
    if (!t) return false;
    setTitle('');
    fireCreate(t);
    return true;
  };

  if (!adding) {
    return (
      <button
        type="button"
        onClick={() => setAdding(true)}
        className="flex w-full items-center gap-2 rounded-lg border border-dashed border-line px-2.5 py-2 text-[13px] text-fg-muted transition-colors hover:border-line-strong hover:bg-layer hover:text-fg"
      >
        <Plus />
        New epic
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2 rounded-lg border border-line bg-surface-inset px-2.5 py-2 focus-within:border-line-strong">
      <TaskTypeGlyph type="epic" color={taskTypeMeta('epic').color} className="shrink-0" />
      <input
        ref={inputRef}
        autoFocus
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Epic title…"
        aria-label="New epic title"
        className="min-w-0 flex-1 bg-transparent text-[13px] text-fg placeholder:text-fg-ghost focus:outline-none"
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            submit(); // stays open with an empty field, ready for the next epic
          } else if (e.key === 'Escape') {
            e.preventDefault();
            escaping.current = true;
            setTitle('');
            setAdding(false);
          }
        }}
        onBlur={() => {
          if (escaping.current) {
            escaping.current = false;
            return;
          }
          submit(); // commit a non-empty title on click-away, then close
          setAdding(false);
        }}
      />
      {pending > 0 ? <Spinner className="h-3.5 w-3.5 shrink-0 text-fg-faint" /> : null}
    </div>
  );
}

/** The header toggle that shows/hides the epic sidebar (mirrors the board-settings cog styling). */
export function EpicSidebarToggle() {
  const { sidebarOpen, toggleSidebar, epics } = useEpics();
  return (
    <button
      type="button"
      onClick={toggleSidebar}
      aria-label={sidebarOpen ? 'Hide epics sidebar' : 'Show epics sidebar'}
      aria-pressed={sidebarOpen}
      title={sidebarOpen ? 'Hide epics' : 'Show epics'}
      className={cn(
        'relative flex h-9 w-9 items-center justify-center rounded-full transition-colors',
        sidebarOpen ? 'bg-layer text-fg-strong' : 'text-fg-muted hover:bg-layer-strong hover:text-fg',
      )}
    >
      <Layers />
      {epics.length > 0 ? (
        <span className="absolute -right-0.5 -top-0.5 min-w-[15px] rounded-full bg-brand px-1 text-[10px] font-bold leading-[15px] text-ink">
          {epics.length}
        </span>
      ) : null}
    </button>
  );
}
